import axios from "axios";
import { checkFor401Error, checkForTokenExpiredError } from "../lib/utils";
import {
  getAuth,
  getCompanyId,
  getCompanyTimeZone,
  removeAuthOnInvalidToken,
} from "./identity.service";
import { getHiringManagerPortalHash } from "./session.identity.service";

export const postWithOutAuth = (url, entity) => {
  return new Promise((resolve, _reject) => {
    axios
      .post(url, entity)
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkFor401Error(e)) {
          return removeAuthOnInvalidToken();
        }
        resolve({
          status: false,
          message: e.response?.data?.message,
          statusCode: e.response?.status,
          error: e,
        });
      });
  });
};

export const postWithAuth = (url, entity) => {
  return new Promise((resolve, _reject) => {
    const auth = getAuth();
    const headers = {
      "content-type": "application/json",
      "x-access-token": auth?.token,
      "x-company-time-zone": getCompanyTimeZone(),
      "x-company-id": getCompanyId() || null,
    };
    axios
      .post(url, entity, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkForTokenExpiredError(e) || checkFor401Error(e)) {
          return removeAuthOnInvalidToken(auth?.token);
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const getWithOutAuth = (url) => {
  return new Promise((resolve, _reject) => {
    axios
      .get(url)
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkFor401Error(e)) {
          return removeAuthOnInvalidToken();
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

/** Hiring Manager portal API calls – auth via magic link hash in x-hiring-manager-portal-hash header */
const getHiringManagerHashHeaders = () => ({
  "content-type": "application/json",
  ...(getHiringManagerPortalHash() ? { "x-hiring-manager-portal-hash": getHiringManagerPortalHash() } : {}),
});

export const getWithHiringManagerHash = (url) => {
  return new Promise((resolve, _reject) => {
    axios
      .get(url, { headers: getHiringManagerHashHeaders() })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkFor401Error(e)) {
          return removeAuthOnInvalidToken();
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const postWithHiringManagerHash = (url, entity) => {
  return new Promise((resolve, _reject) => {
    axios
      .post(url, entity, { headers: getHiringManagerHashHeaders() })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkFor401Error(e)) {
          return removeAuthOnInvalidToken();
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const getWithAuth = (url) => {
  const auth = getAuth();
  const headers = {
    "content-type": "application/json",
    "x-access-token": auth?.token,
    "x-company-time-zone": getCompanyTimeZone(),
    "x-company-id": getCompanyId() || null,
  };
  return new Promise((resolve, _reject) => {
    axios
      .get(url, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        console.error('API request failed:', e);
        if (typeof window === "object") {
          if (checkForTokenExpiredError(e) || checkFor401Error(e)) {
            return removeAuthOnInvalidToken(auth?.token);
          }
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const getWithAuthByRefreshToken = (url) => {
  const headers = {
    "content-type": "application/json",
  };
  return new Promise((resolve, _reject) => {
    axios
      .get(url, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        console.error("The error in getWithAuthByRefreshToken for url ", url, " is ", e);
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const postWithAuthByRefreshToken = (url, entity) => {
  const headers = {
    "content-type": "application/json",
  };
  return new Promise((resolve, _reject) => {
    axios
      .post(url, entity, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        console.error("The error in postWithAuthByRefreshToken for url ", url, " is ", e);
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const getWithAuthWithToken = (url) => {
  const auth = getAuth();

  const headers = {
    "content-type": "application/json",
    "x-access-token": auth?.token,
    "x-company-time-zone": getCompanyTimeZone(),
    "x-company-id": getCompanyId() || null,
  };
  return new Promise((resolve, _reject) => {
    axios
      .get(url, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkFor401Error(e)) {
          return removeAuthOnInvalidToken(auth?.token);
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const deleteWithAuth = (url, data) => {
  const auth = getAuth();
  const headers = {
    "content-type": "application/json",
    "x-access-token": auth?.token,
  };

  return new Promise((resolve, _reject) => {
    axios
      .delete(url, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkForTokenExpiredError(e) || checkFor401Error(e)) {
          return removeAuthOnInvalidToken(auth?.token);
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const putWithAuth = (url, data) => {
  const auth = getAuth();
  const headers = {
    "content-type": "application/json",
    "x-access-token": auth?.token,
    "x-company-time-zone": getCompanyTimeZone(),
    "x-company-id": getCompanyId() || null,
  };
  return new Promise((resolve, _reject) => {
    axios
      .put(url, data, { headers })
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkForTokenExpiredError(e) || checkFor401Error(e)) {
          return removeAuthOnInvalidToken(auth?.token);
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const putWithOutAuth = (url, entity) => {
  return new Promise((resolve, _reject) => {
    axios
      .put(url, entity)
      .then((response) => {
        if (response && response.data) {
          resolve(response.data);
        }
      })
      .catch((e) => {
        if (checkFor401Error(e)) {
          return removeAuthOnInvalidToken();
        }
        resolve({ status: false, message: e.message, error: e });
      });
  });
};

export const sendBeacon = (url, data) => {
  var params = new URLSearchParams(data);

  return navigator.sendBeacon(url, params);
};
